import { z } from 'zod';
import { zId } from './primitives.js';
import { zPieceStatus, zReturnLineState } from './consignment.js';

/**
 * Stock control over pieces a retailer holds — CN-09 (recall), CN-10 (lost, damaged or stolen) and
 * CN-11 (stock verification). A-43's second half; R-20 for a retailer's loss report.
 *
 * Each requirement has a policy question the client has not answered, and none of them is
 * answered here. Each is a portal setting (SC-07), read from A-36, named beside its shape:
 *
 *   D20      what triggers a recall            `recallTrigger`          default NONE
 *   D39/D81  the stated outcome of a loss      `lossDefaultOutcome`     default none — Admin chooses
 *   D40      how often a retailer is counted   `verificationCycleDays`  default none — on demand
 *
 * Money is a decimal string everywhere (NF-04), and amounts are per currency, never summed across.
 */

type Amount = { readonly currency: string; readonly amount: string };
const zDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'a date, YYYY-MM-DD');

// ------------------------------------------------------------------ the three settings

/**
 * D20 ❓ — what an ageing piece triggers.
 *   NONE    nothing does. Admin may still recall any held piece by hand (CN-09).
 *   ALERT   a piece held `afterDays` or more is listed as due on A-43; a person decides.
 *   RECALL  the daily job recalls it — a forced return, raised by nobody, received as any other.
 * Days are counted in the portal's calendar (D49), with D80's one definition of days held.
 */
export const zRecallTrigger = z.discriminatedUnion('type', [
  z.object({ type: z.literal('NONE') }).strict(),
  z.object({ type: z.literal('ALERT'), afterDays: z.number().int().min(1).max(3650) }).strict(),
  z.object({ type: z.literal('RECALL'), afterDays: z.number().int().min(1).max(3650) }).strict(),
]);
export type RecallTrigger = z.infer<typeof zRecallTrigger>;

export const zLossKind = z.enum(['LOST', 'DAMAGED', 'STOLEN']);
export type LossKind = z.infer<typeof zLossKind>;

/** CN-10's outcomes, and FOUND for a piece that turns up. FOUND is only for a piece that is gone, not damaged. */
export const zLossOutcome = z.enum(['CHARGED', 'INSURANCE_CLAIM', 'WRITTEN_OFF', 'FOUND']);
export type LossOutcome = z.infer<typeof zLossOutcome>;

/** D39/D81 ❓ — the outcome A-43 offers first. Null: none is offered, and Admin picks every time. */
export const zLossDefaultOutcome = z.enum(['CHARGED', 'INSURANCE_CLAIM', 'WRITTEN_OFF']).nullable();

/** D40 ❓ — days between counts at one retailer. Null: no cycle; a count is run when someone asks. */
export const zVerificationCycleDays = z.number().int().min(7).max(730).nullable();

export interface StockPolicy {
  readonly recallTrigger: RecallTrigger;
  readonly lossDefaultOutcome: z.infer<typeof zLossDefaultOutcome>;
  readonly verificationCycleDays: z.infer<typeof zVerificationCycleDays>;
}

// ------------------------------------------------------------------ CN-10 — reporting a loss

/**
 * Report a held piece lost, damaged or stolen — the retailer from R-20, or Admin from A-43. The
 * piece moves at once (HELD → LOST, or → DAMAGED) with a movement row (CN-12): it is no longer
 * stock the retailer can sell. `account` is required because Admin decides the outcome against it.
 */
export const zLossReport = z.object({
  pieceId: zId,
  kind: zLossKind,
  account: z.string().trim().min(10, 'say what happened, in a sentence').max(1000),
  /** A-43 only: the count (CN-11) that found it, so the report and the count read together. */
  verificationId: zId.optional(),
});
export type LossReport = z.infer<typeof zLossReport>;

/**
 * CN-10 — Admin records the outcome with a reason. **No default is applied on the server**: the
 * outcome is a required field whatever `lossDefaultOutcome` says (D39/D81), because the setting
 * only decides what the screen offers first. CHARGED posts a LOSS_CHARGE to the ledger.
 */
export const zLossOutcomeRecord = z.object({
  outcome: zLossOutcome,
  reason: z.string().trim().min(10, 'say why, in a sentence — this may be a charge').max(500),
});
export type LossOutcomeRecord = z.infer<typeof zLossOutcomeRecord>;

export interface LossReportView {
  readonly id: string;
  readonly retailerId: string;
  readonly retailerName: string;
  readonly pieceId: string;
  readonly jobNumber: string;
  readonly description: string;
  readonly wholesaleValue: string;
  readonly currency: string;
  /** What the piece is now — LOST, DAMAGED, or the outcome's status. The movement is the authority. */
  readonly pieceStatus: z.infer<typeof zPieceStatus>;
  readonly kind: LossKind;
  readonly account: string;
  readonly reportedByType: 'RETAILER' | 'INTERNAL';
  readonly reportedAt: string;
  readonly verificationId: string | null;
  readonly outcome: LossOutcome | null;
  readonly outcomeReason: string | null;
  readonly outcomeAt: string | null;
  /** The outcomes that may be recorded from here; empty when it is finished. */
  readonly next: readonly LossOutcome[];
}

export interface LossQueue {
  readonly asOf: string;
  readonly lossDefaultOutcome: z.infer<typeof zLossDefaultOutcome>;
  /** Open first, then the most recent. */
  readonly reports: readonly LossReportView[];
}

/** R-20 — what a retailer has reported, and what became of it. A charge is shown as what it is. */
export interface MyLosses {
  readonly reports: readonly LossReportView[];
}

// ------------------------------------------------------------------ CN-11 — stock verification

export const zVerificationResult = z.enum(['PRESENT', 'MISSING', 'DAMAGED']);
export type VerificationResult = z.infer<typeof zVerificationResult>;

/**
 * Record the count. Every piece on the list must have a result: a count with gaps is not a count,
 * and a piece left blank would read later as "not checked" and "present" to two different people.
 */
export const zVerificationComplete = z.object({
  countedOn: zDay,
  results: z.array(z.object({
    pieceId: zId,
    result: zVerificationResult,
    note: z.string().trim().max(500).optional(),
  })).min(1).max(2000),
  remarks: z.string().trim().max(2000).optional(),
});
export type VerificationComplete = z.infer<typeof zVerificationComplete>;

export interface VerificationLineView {
  readonly pieceId: string;
  readonly jobNumber: string;
  readonly description: string;
  readonly wholesaleValue: string;
  readonly currency: string;
  readonly result: VerificationResult | null;
  readonly note: string | null;
  /** What the piece is NOW. A line found MISSING whose piece is now LOST has been reported. */
  readonly pieceStatus: z.infer<typeof zPieceStatus>;
  /** An open or decided loss report against this piece raised from this count, if any. */
  readonly lossReportId: string | null;
}

export interface VerificationView {
  readonly id: string;
  readonly retailerId: string;
  readonly startedAt: string;
  readonly countedOn: string | null;
  readonly completedAt: string | null;
  readonly remarks: string | null;
  readonly lines: readonly VerificationLineView[];
  /** MISSING or DAMAGED lines. Zero on an open count. */
  readonly discrepancies: number;
}

/** One piece the retailer should hold, for A-43's stock list and the count sheet. */
export interface StockPiece {
  readonly pieceId: string;
  readonly jobNumber: string;
  readonly description: string;
  readonly goldColour: string | null;
  readonly ringSize: string | null;
  readonly wholesaleValue: string;
  readonly currency: string;
  readonly receivedAt: string | null;
  /** D80 — the one definition, from My Inventory's read. */
  readonly daysHeld: number | null;
  readonly returnRequest: { readonly state: z.infer<typeof zReturnLineState>; readonly origin: 'RETAILER' | 'MIRAKI' } | null;
  /** D20 — held at least the trigger's `afterDays`, and on no live request. Always false under NONE. */
  readonly dueForRecall: boolean;
}

export interface StockRetailerRow {
  readonly retailerId: string;
  readonly retailerName: string;
  readonly accountStatus: string;
  readonly piecesHeld: number;
  readonly heldValue: readonly Amount[];
  readonly oldestDaysHeld: number | null;
  readonly dueForRecall: number;
  readonly openLosses: number;
  readonly lastCountedOn: string | null;
  /** D40 — null when the portal has no cycle; otherwise whether a count is due (never counted counts as due). */
  readonly countDue: boolean | null;
  readonly openVerificationId: string | null;
}

export interface StockOverview {
  readonly asOf: string;
  readonly policy: StockPolicy;
  /** Every retailer holding at least one piece, or with an open loss or an open count. */
  readonly retailers: readonly StockRetailerRow[];
}

export interface RetailerStock {
  readonly asOf: string;
  readonly policy: StockPolicy;
  readonly retailer: { readonly id: string; readonly name: string; readonly accountStatus: string };
  /** HELD pieces only: the list a count is checked against. */
  readonly pieces: readonly StockPiece[];
  /** Newest first; the open one, if any, first of all. */
  readonly verifications: readonly VerificationView[];
  readonly losses: readonly LossReportView[];
}
